import { connect } from 'react-redux';
import uniq from 'lodash.uniq';
import isEmpty from 'lodash.isempty';
import { push } from 'redux-first-history';
import compact from 'lodash.compact';
import { getUrlParams, getUrlString, updateSearchObject } from '../../../../utils/shared/urlUtils';
import {
    clearBasicComprehensionWebEntryData,
    invalidateResult,
    prepareAndCreateResult,
} from '../../../../api/basicComprehensionWebEntry/shared/basicComprehensionWebEntryActions';
import { selectors } from '../../../../root/reading/root';
import { fetchD8Results } from '../../../../api/results/reading/resultActions';
import fetchStaff from '../../../../api/staff/reading/staffActions';
import { fetchFormIdsForMultipleGrades } from '../../../../api/forms/shared/formsActions';
import {
    BASIC_COMPREHENSION_SIDE_PANEL,
    PERIOD,
} from '../../../../constants/shared/CommonConstants';
import { LICENSE } from '../../../../constants/reading/CommonConstants';
import { getGradeTitle, UNSUPPORTED_GRADES } from '../../../../constants/shared/GradesConstants';
import { TITLE } from '../../../../constants/shared/PMConstants';
import MAZEClient from '../../../../api/clients/reading/MAZEClient';
import { getD8Result } from '../../../../api/results/reading/resultReducerParsers';
import { MEASURE } from '../../../../constants/reading/MeasuresConstants';
import WebEntryPanel from '../../../../components/webEntryPanel/WebEntryPanel';
import {
    getCellsData,
    getSpecificDataByPanelType,
} from '../../../../components/webEntryPanel/utils';
import { SUPPORTED_GRADES_WITH_BC } from '../../../../constants/shared/WebEntryConstants';

const { PM, EOY, SUMMER } = PERIOD;
const { PM_TITLE } = TITLE;

const mapStateToProps = (state, ownProps) => {
    const location = selectors.getLocation(state);
    const staffIds = uniq(selectors.getStaffIdsFromStudentsMazeResults(state));
    const staffData = compact(selectors.getStaffByIds(state, staffIds)) || [];
    const idsToFetch =
        isEmpty(staffData) || staffData.some(item => !item)
            ? staffIds.filter(id => !staffData.find(staff => staff && staff.uid === id))
            : [];
    const isPmPanelType = ownProps.panelType === PM_TITLE;
    const period = isPmPanelType ? PM : selectors.getSelectedPeriod(state);
    const licensingInfo = selectors.getLicensingInfo(state);
    const currentPeriod = selectors.getCurrentPeriod(state);
    const tabPeriod = getUrlParams(location).period;
    const isSecurePeriod =
        licensingInfo.includes(LICENSE.SCHEDULER) &&
        (currentPeriod === EOY || currentPeriod === SUMMER) &&
        (tabPeriod === EOY || currentPeriod === SUMMER);
    return {
        location,
        studentsByGradeData: selectors.getChosenClassStudentsMazeResultsByGrade(state),
        staffIds,
        idsToFetch,
        userId: selectors.getUserId(state),
        formIdsData: selectors.getFormIdsByPeriod(state, period),
        isPmPanelType,
        panelType: ownProps.panelType,
        period,
        isPanelUpdating: selectors.isPanelUpdating(state),
        rowStatuses: selectors.getAllRowsStatuses(state),
        isStaffFetching: selectors.isStaffFetching(state),
        windowDates: selectors.getWindowDatesForSelectedPeriod(state),
        isChosenSchoolWindowDatesStrictlyEnforced:
            selectors.isChosenSchoolWindowDatesStrictlyEnforced(state),
        className: BASIC_COMPREHENSION_SIDE_PANEL,
        isSecurePeriod,
    };
};

const mapDispatchToProps = dispatch => ({
    fetchStaff: staffUids => dispatch(fetchStaff(staffUids)),
    fetchFormIds: period =>
        dispatch(fetchFormIdsForMultipleGrades(SUPPORTED_GRADES_WITH_BC, period, MAZEClient)),
    closeComponent: location =>
        dispatch(
            push(
                getUrlString(location.pathname, {
                    ...updateSearchObject(location, ['webEntryOpened', 'isEditable']),
                }),
            ),
        ),
    fetchResults: () => {
        dispatch(fetchD8Results());
    },
    clearWebEntryData: () => dispatch(clearBasicComprehensionWebEntryData()),
    invalidateResult: (studentId, resultId) =>
        dispatch(invalidateResult(studentId, resultId, MAZEClient, MEASURE.MAZE)),
    prepareAndCreateResult: (
        fields,
        grade,
        formId,
        studentId,
        period,
        temporalStudentId,
        temporalInstId,
    ) =>
        dispatch(
            prepareAndCreateResult({
                fields,
                grade,
                formId,
                studentId,
                period,
                getResult: getD8Result,
                client: MAZEClient,
                temporalStudentId,
                temporalInstId,
            }),
        ),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
    const {
        isPanelUpdating,
        windowDates,
        formIdsData,
        idsToFetch,
        studentsByGradeData,
        isSecurePeriod,
    } = stateProps;

    // do not show unsuported grades
    const filteredStudentsByGradeData = studentsByGradeData.filter(
        item => !UNSUPPORTED_GRADES.includes(item.grade),
    );

    const webEntryClassName = 'maze';
    const mainTitle = 'Maze';
    const {
        assessmentType,
        headerTitle,
        panelClassName,
        headerCells,
        typeOfCells,
        getErrors,
        isSecureHeaderText,
    } = getSpecificDataByPanelType(ownProps.panelType, mainTitle, isSecurePeriod);

    return {
        ...stateProps,
        ...dispatchProps,

        webEntryPanelConfig: {
            webEntryClassName,
            assessmentType,
            getErrors,
            panelClassName,
            headerSection: {
                headerTitle,
                isPanelUpdating,
                isSecureHeaderText,
                mainTitle,
            },
            tableData: {
                headerCells,
                rows: {
                    windowDates,
                    formIdsData,
                    getCellsData: ({
                        grade,
                        studentInfo,
                        result,
                        error,
                        handlers,
                        elementsRef,
                        rowData,
                        rowConfig,
                        setIsRowFocused,
                        setAlertType,
                    }) => {
                        const gradesWithBC =
                            ownProps.supportedGradesWithBC || ownProps.gradesWithBC;
                        const formGradeDropdownItems = gradesWithBC.map(gradeItem => ({
                            uid: gradeItem,
                            name: getGradeTitle(gradeItem),
                        }));
                        const isWebEntryGrade = !ownProps.gradesWithoutBC.includes(grade);
                        const cells = getCellsData({
                            grade,
                            studentInfo,
                            result,
                            error,
                            handlers,
                            elementsRef,
                            rowData,
                            rowConfig,
                            setIsRowFocused,
                            setAlertType,
                            isWebEntryGrade,
                            formIdsData,
                            formGradeDropdownItems,
                            webEntryClassName,
                        });
                        return cells[typeOfCells];
                    },
                },
            },
        },
        fetchStaff: () => dispatchProps.fetchStaff(idsToFetch),
        studentsByGradeData: filteredStudentsByGradeData,
    };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(WebEntryPanel);
